import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSQLiteService } from '../hooks/useSQLiteService';
import { SQLiteService } from '../services/SQLiteService';
import { DocumentEditorWithInMemoryState } from './DocumentEditorWithInMemoryState';
import { serverUrl } from '../config/environment';

interface DocumentState {
  exists: boolean;
  isLocal: boolean;
  isRemote: boolean;
  title: string;
  pages: any[];
  loading: boolean;
  error: string | null;
}

const DocumentView: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  
  const [documentState, setDocumentState] = useState<DocumentState>({
    exists: false,
    isLocal: false,
    isRemote: false,
    title: '',
    pages: [],
    loading: true,
    error: null
  });
  
  // Force DocumentEditor to refresh after partial load
  const [editorRefreshKey, setEditorRefreshKey] = useState(0);

  const [syncStatus, setSyncStatus] = useState<{
    syncing: boolean;
    message: string;
    progress?: number;
  }>({
    syncing: false,
    message: 'Ready'
  });

  // Only initialize SQLite service if document exists locally AND loading is complete
  const sqliteServiceForEditor = useSQLiteService(
    (documentState.isLocal && !documentState.loading) ? documentId : undefined
  );

  // Initialize hybrid sync when document has both local and remote
  // Memoize to prevent object recreation causing component remounts


  useEffect(() => {
    if (documentId) {
      checkDocumentStatus();
    }
  }, [documentId]);

  const checkDocumentStatus = async () => {
    if (!documentId) return;

    setDocumentState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // First, check if document database exists locally (without creating it)
      const localDatabases = await SQLiteService.listDocumentDatabases();
      const hasLocalDocument = localDatabases.some(db => db.documentId === documentId);
      
      if (hasLocalDocument) {
        // Local document exists - load it immediately (don't wait for server check)
        setDocumentState({
          exists: true,
          isLocal: true,
          isRemote: false, // Will be updated when server check completes
          title: '',
          pages: [],
          loading: false,
          error: null
        });
        
        
        // Check server status in background (non-blocking)
        checkServerDocument().then(hasRemoteDocument => {
          if (hasRemoteDocument) {
            // Update state to enable sync
            setDocumentState(prev => ({
              ...prev,
              isRemote: true
            }));
          } else {
          }
        }).catch(error => {
          console.warn('Background server check failed:', error);
          // Keep isRemote: false
        });
        
      } else {
        // No local document - check server first before deciding what to show
        
        checkServerDocument().then(hasRemoteDocument => {
          if (hasRemoteDocument) {
            // Only remote exists - show download/partial load options
            setDocumentState({
              exists: true,
              isLocal: false,
              isRemote: true,
              title: '',
              pages: [],
              loading: false,
              error: null
            });
          } else {
            // Neither exists - show create locally option
            setDocumentState({
              exists: false,
              isLocal: false,
              isRemote: false,
              title: '',
              pages: [],
              loading: false,
              error: null
            });
          }
        }).catch(error => {
          console.warn('Server check failed, assuming document does not exist:', error);
          // Assume document doesn't exist - show create locally option
          setDocumentState({
            exists: false,
            isLocal: false,
            isRemote: false,
            title: '',
            pages: [],
            loading: false,
            error: null
          });
        });
      }
      
    } catch (error) {
      setDocumentState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to check document status'
      }));
    }
  };

  const checkServerDocument = async (): Promise<boolean> => {
    if (!documentId) return false;

    try {
      
      // Create AbortController with 3-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn(`[DEBUG] Server check timeout after 3 seconds`);
        controller.abort();
      }, 3000);

      try {
        // Use lightweight exists endpoint instead of expensive root-hash computation
        const response = await fetch(`${serverUrl}/api/documents/${documentId}/exists`, {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          // Simple boolean check - much more lightweight than hash computation
          return data.exists === true;
        } else if (response.status === 404) {
          // Document doesn't exist on server
          return false;
        } else {
          // Server error - assume document doesn't exist
          console.warn(`[DEBUG] Server error checking document: ${response.status}`);
          return false;
        }
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.warn(`[DEBUG] Server check aborted due to timeout - assuming document doesn't exist on server`);
        return false;
      }
      // Network error or server down - assume document doesn't exist
      console.warn('[DEBUG] Unable to check server status:', error);
      return false;
    }
  };


  const handleCreateLocally = async () => {
    if (!documentId) return;

    setSyncStatus({ syncing: true, message: 'Creating document locally...' });

    try {
      // Create a new SQLite service instance for this document
      const newSqliteService = new SQLiteService();
      await newSqliteService.initialize(`${documentId}.db`);

      // Create document object
      await newSqliteService.insertOrUpdateProperty(documentId, 'object.metadata', JSON.stringify({
        type: 'document',
        parent_id: null
      }));
      
      await newSqliteService.insertOrUpdateProperty(documentId, 'title', documentId);

      // Create first page
      const firstPageId = `${documentId}-page-1`;
      await newSqliteService.insertOrUpdateProperty(firstPageId, 'object.metadata', JSON.stringify({
        type: 'page',
        parent_id: documentId
      }));
      
      await newSqliteService.insertOrUpdateProperty(firstPageId, 'title', 'Page 1');
      await newSqliteService.insertOrUpdateProperty(firstPageId, 'position', '1');

      setSyncStatus({ syncing: false, message: 'Document created successfully!' });
      
      // Refresh document state
      await checkDocumentStatus();
    } catch (error) {
      setSyncStatus({ 
        syncing: false, 
        message: `Failed to create document: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    }
  };

  const handleDownloadDocument = async () => {
    if (!documentId) return;

    setSyncStatus({ syncing: true, message: 'Downloading complete database from server...', progress: 20 });

    try {
      // Step 1: Download the complete database export from server
      const databaseResponse = await fetch(`${serverUrl}/api/documents/${documentId}/download-database`);
      if (!databaseResponse.ok) {
        throw new Error(`Failed to download database: ${databaseResponse.status}`);
      }
      
      const databaseExport = await databaseResponse.json();
      const objects = databaseExport.objects || [];
      const properties = databaseExport.properties || [];

      if (objects.length === 0) {
        throw new Error('No objects found in remote document');
      }

      setSyncStatus({ syncing: true, message: `Creating local database with ${objects.length} objects and ${properties.length} properties...`, progress: 50 });

      // Step 2: Create a new SQLite database and populate it directly
      const newSqliteService = new SQLiteService();
      
      console.log(`🚀 [DOWNLOAD] Starting database creation for ${documentId}...`);
      const dbStartTime = performance.now();
      
      await newSqliteService.initializeFromDatabaseExport(documentId, databaseExport);
      
      const dbTime = performance.now() - dbStartTime;
      console.log(`🚀 [DOWNLOAD] Database creation completed in ${dbTime.toFixed(1)}ms (${(dbTime/1000).toFixed(1)}s)`);

      const totalPropertiesInserted = properties.length;

      setSyncStatus({ syncing: true, message: 'Computing hashes and finalizing...', progress: 90 });

      // Process dirty objects to compute hashes (objects were marked dirty by importProperties)
      await newSqliteService.processDirtyObjects();

      setSyncStatus({ syncing: true, message: 'Finalizing download...', progress: 95 });

      // Step 8: Save the database
      await newSqliteService.saveDatabase();
      await newSqliteService.close();

      setSyncStatus({ 
        syncing: false, 
        message: `✅ Download completed! ${totalPropertiesInserted} properties downloaded from ${objects.length} objects.` 
      });

      // Step 9: Force document state recheck to detect local+remote status
      setTimeout(() => {
        checkDocumentStatus();
      }, 500);

      // Step 10: Refresh the page to show editing UI
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Download error details:', error);
      setSyncStatus({ 
        syncing: false, 
        message: `Download failed: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    }
  };

  const handleLoadPartially = async () => {
    if (!documentId) return;

    setSyncStatus({ syncing: true, message: 'Creating local database...', progress: 10 });

    try {
      // Step 1: Create a new local SQLite database
      const newSqliteService = new SQLiteService();
      await newSqliteService.initialize(`${documentId}.db`);

      setSyncStatus({ syncing: true, message: 'Fetching document structure...', progress: 25 });

      // Step 2: Fetch document subtree with depth limit (document + pages only)
      // Depth 2 = document (depth 0) + pages (depth 1), excluding content blocks (depth 2+)
      const subtreesResponse = await fetch(`${serverUrl}/api/documents/${documentId}/fetch-subtrees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          root_object_ids: [documentId], // Start from the document root
          max_depth: 1 // Document (0) + Pages (1) only
        })
      });

      if (!subtreesResponse.ok) {
        throw new Error(`Failed to fetch subtrees: ${subtreesResponse.status}`);
      }
      const subtreesData = await subtreesResponse.json();
      
      // Extract objects with properties from subtrees response
      const objectsWithProperties: Array<{object_id: string, object_type: string, properties: any[]}> = [];
      if (subtreesData.subtrees && subtreesData.subtrees.length > 0) {
        for (const subtree of subtreesData.subtrees) {
          for (const obj of subtree.objects) {
            objectsWithProperties.push({
              object_id: obj.object_id,
              object_type: obj.object_type,
              properties: obj.properties
            });
          }
        }
      }
      

      setSyncStatus({ syncing: true, message: 'Saving structural data to local database...', progress: 75 });

      // Step 6: Import structural properties preserving original server metadata
      const allProperties = [];
      for (const objWithProps of objectsWithProperties) {
        if (!objWithProps.properties || !Array.isArray(objWithProps.properties)) {
          continue;
        }
        
        for (const property of objWithProps.properties) {
          const objectId = property.object_id || objWithProps.object_id;
          if (objectId) {
            // Preserve all original metadata from server
            allProperties.push({ ...property, object_id: objectId });
          }
        }
      }
      
      // Use importProperties to preserve all original timestamps and metadata
      // This will also rebuild objects table and mark objects as dirty
      await newSqliteService.importProperties(allProperties);
      const totalPropertiesInserted = allProperties.length;

      setSyncStatus({ syncing: true, message: 'Computing hashes and finalizing...', progress: 90 });

      // Process dirty objects to compute hashes (objects were marked dirty by importProperties)
      await newSqliteService.processDirtyObjects();

      // Step 9: Save the database
      await newSqliteService.saveDatabase();
      await newSqliteService.close();

      setSyncStatus({ 
        syncing: false, 
        message: `✅ Partial load completed! ${totalPropertiesInserted} properties from ${objectsWithProperties.length} structural objects loaded (depth: ${subtreesData.max_depth_actual || 1}).` 
      });

      // Step 10: Force DocumentEditor to refresh immediately to show new data
      setEditorRefreshKey(prev => prev + 1);

      // Step 11: Force document state recheck to detect local+remote status
      setTimeout(() => {
        checkDocumentStatus();
      }, 500);

      // Step 12: The new DocumentEditorWithInMemoryState will automatically:
      // 1. Load the new data from database into in-memory state
      // 2. Register with syncStateIntegration for future sync changes
      // 3. Display the UI immediately with the partial data
      
      
      // Step 12: Optional - trigger a depth sync for the first page to load content blocks
      // This happens automatically when user clicks on a page due to onPageVisible callback
      setTimeout(() => {
        if (objectsWithProperties.length > 1) {
          const firstPage = objectsWithProperties.find(obj => obj.object_type === 'page');
          if (firstPage) {
            // Sync is now handled automatically by SimpleSyncEngine in DocumentEditorWithInMemoryState
          }
        }
      }, 1000);

      // No more page reload needed - in-memory state handles everything!

    } catch (error) {
      console.error('Partial download error details:', error);
      setSyncStatus({ 
        syncing: false, 
        message: `Partial load failed: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    }
  };

  const handlePushToCloud = async () => {
    if (!documentId || !sqliteServiceForEditor) return;

    setSyncStatus({ syncing: true, message: 'Checking server status...', progress: 0 });

    try {
      // First, check if document already exists on server
      const hasRemoteDocument = await checkServerDocument();
      
      if (hasRemoteDocument) {
        setSyncStatus({ 
          syncing: false, 
          message: 'Document already exists on server. Use sync instead.' 
        });
        return;
      }

      setSyncStatus({ syncing: true, message: 'Pushing document to cloud...', progress: 25 });

      // Get all local properties to push
      const allProperties = await sqliteServiceForEditor.getAllProperties();
      
      if (allProperties.length === 0) {
        throw new Error('No local data to push');
      }

      setSyncStatus({ syncing: true, message: 'Uploading properties...', progress: 50 });

      // Push properties to server using the sync endpoint
      const response = await fetch(`${serverUrl}/api/documents/${documentId}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          client_state: { last_sync_timestamp: 0 },
          changes: allProperties 
        })
      });

      if (!response.ok) {
        throw new Error(`Push failed: ${response.status} ${response.statusText}`);
      }

      setSyncStatus({ syncing: true, message: 'Verifying upload...', progress: 75 });

      // Verify the push was successful by checking server again
      const verifyResponse = await checkServerDocument();
      
      if (!verifyResponse) {
        throw new Error('Push verification failed');
      }

      setSyncStatus({ 
        syncing: false, 
        message: `✅ Successfully pushed ${allProperties.length} properties to cloud!` 
      });

      // Auto-switch to sync-enabled mode
      setDocumentState(prev => ({
        ...prev,
        isRemote: true
      }));


    } catch (error) {
      setSyncStatus({ 
        syncing: false, 
        message: `Push failed: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    }
  };

  if (documentState.loading) {
    return (
      <div className="document-loading">
        <div className="loading-spinner"></div>
        <h2>Loading Document...</h2>
        <p>Checking document status for: <code>{documentId}</code></p>
      </div>
    );
  }

  if (documentState.error) {
    return (
      <div className="document-error">
        <h2>Error</h2>
        <p>{documentState.error}</p>
        <div className="error-actions">
          <button onClick={() => navigate('/')} className="back-btn">
            ← Back to Home
          </button>
          <button onClick={checkDocumentStatus} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (documentState.isLocal) {
    // Document exists locally - show editor
    return (
      <div className="document-view">
        {sqliteServiceForEditor && (
          <>
            <DocumentEditorWithInMemoryState 
              documentId={documentId!}
              enableSync={documentState.isLocal && documentState.isRemote}
              isRemote={documentState.isRemote}
              sqliteService={sqliteServiceForEditor}
              onSyncStatusChange={(isRemote) => {
                setDocumentState(prev => ({
                  ...prev,
                  isRemote: isRemote
                }));
              }}
            />
            
            {/* Keep the old DocumentEditor commented for fallback */}
            {/* Old DocumentEditor component - replaced by DocumentEditorWithInMemoryState with SimpleSyncEngine */}
          </>
        )}

        {/* Sync activities now shown in header dropdown */}
      </div>
    );
  }

  // Document doesn't exist locally
  return (
    <div className="document-setup">
      <header className="setup-header">
        <button onClick={() => navigate('/')} className="back-btn">
          ← Back to Home
        </button>
        <h1>Document: {documentId}</h1>
      </header>

      <div className="setup-content">
        {documentState.isRemote ? (
          // Document exists on server but not locally
          <div className="server-document">
            <div className="setup-card">
              <h2>📄 Document Found on Server</h2>
              <p>This document exists on the server but not locally.</p>
              
              <div className="sync-options">
                <button 
                  onClick={handleDownloadDocument}
                  disabled={syncStatus.syncing}
                  className="download-btn primary"
                >
                  {syncStatus.syncing ? 'Downloading...' : '⬇️ Download Document'}
                </button>
                
                <button 
                  onClick={handleLoadPartially}
                  disabled={syncStatus.syncing}
                  className="partial-btn secondary"
                >
                  {syncStatus.syncing ? 'Loading...' : '📋 Load Partially'}
                </button>
              </div>
              
              <div className="option-descriptions">
                <div className="option-desc">
                  <strong>Download Document:</strong> Fetches the complete document with all pages and content.
                </div>
                <div className="option-desc">
                  <strong>Load Partially:</strong> Fetches document structure (depth 1: document + pages) for faster loading.
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Document doesn't exist anywhere
          <div className="new-document">
            <div className="setup-card">
              <h2>📝 Create New Document</h2>
              <p>This document doesn't exist locally or on the server. Create it to get started.</p>
              
              <button 
                onClick={handleCreateLocally}
                disabled={syncStatus.syncing}
                className="create-btn primary"
              >
                {syncStatus.syncing ? 'Creating...' : '✨ Create Locally'}
              </button>
              
              <div className="option-descriptions">
                <div className="option-desc">
                  This will create a new document with one page in your local database.
                  You can sync it to the server later.
                </div>
              </div>
            </div>
          </div>
        )}

        {syncStatus.message !== 'Ready' && (
          <div className="sync-status">
            <div className={`sync-message ${syncStatus.syncing ? 'syncing' : ''}`}>
              {syncStatus.syncing && <div className="progress-spinner"></div>}
              {syncStatus.message}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .document-setup {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .document-view {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .document-loading,
        .document-error {
          text-align: center;
          padding: 4rem 2rem;
          max-width: 600px;
          margin: 0 auto;
        }

        .loading-spinner,
        .progress-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #ecf0f1;
          border-top: 4px solid #3498db;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        .progress-spinner {
          width: 20px;
          height: 20px;
          border-width: 2px;
          margin: 0 0.5rem 0 0;
        }

        .setup-header,
        .document-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #ecf0f1;
        }

        .back-btn {
          padding: 0.5rem 1rem;
          background-color: #95a5a6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .back-btn:hover {
          background-color: #7f8c8d;
          transform: translateX(-2px);
        }

        .setup-header h1,
        .document-header h1 {
          color: #2c3e50;
          margin: 0;
          flex: 1;
        }

        .document-id {
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
          background-color: #ecf0f1;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          color: #7f8c8d;
        }

        .sync-status-badge {
          font-size: 0.85rem;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-weight: 500;
        }

        .sync-enabled {
          background-color: #e8f5e8;
          color: #27ae60;
        }

        .sync-disabled {
          background-color: #fef9e7;
          color: #f39c12;
        }

        .local-only-controls {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .push-btn {
          padding: 0.5rem 1rem;
          background-color: #3498db;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .push-btn:hover:not(:disabled) {
          background-color: #2980b9;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
        }

        .push-btn:disabled {
          background-color: #bdc3c7;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }


        .setup-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          padding: 2rem;
          text-align: center;
          margin-bottom: 2rem;
        }

        .setup-card h2 {
          color: #2c3e50;
          margin-bottom: 1rem;
          font-size: 1.5rem;
        }

        .setup-card p {
          color: #7f8c8d;
          margin-bottom: 2rem;
          font-size: 1.1rem;
        }

        .sync-options {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-bottom: 2rem;
        }

        .download-btn,
        .partial-btn,
        .create-btn {
          padding: 1rem 2rem;
          border: none;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 180px;
        }

        .primary {
          background-color: #3498db;
          color: white;
        }

        .primary:hover:not(:disabled) {
          background-color: #2980b9;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(52, 152, 219, 0.3);
        }

        .secondary {
          background-color: #95a5a6;
          color: white;
        }

        .secondary:hover:not(:disabled) {
          background-color: #7f8c8d;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(149, 165, 166, 0.3);
        }

        .download-btn:disabled,
        .partial-btn:disabled,
        .create-btn:disabled {
          background-color: #bdc3c7;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .option-descriptions {
          text-align: left;
          margin-top: 2rem;
        }

        .option-desc {
          background-color: #f8f9fa;
          border-left: 4px solid #3498db;
          padding: 1rem;
          margin-bottom: 1rem;
          border-radius: 0 6px 6px 0;
          font-size: 0.95rem;
          color: #2c3e50;
        }

        .option-desc strong {
          color: #2980b9;
        }

        .sync-status {
          margin: 1rem 0;
        }

        .sync-progress,
        .sync-message {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          background-color: #e8f4f8;
          border-radius: 8px;
          color: #2980b9;
          font-weight: 500;
        }

        .sync-message.syncing {
          background-color: #fff3cd;
          color: #856404;
        }

        .error-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-top: 2rem;
        }

        .retry-btn {
          padding: 0.75rem 1.5rem;
          background-color: #e74c3c;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 1rem;
        }

        .retry-btn:hover {
          background-color: #c0392b;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .document-setup,
          .document-view {
            padding: 1rem;
          }

          .sync-options {
            flex-direction: column;
            align-items: center;
          }

          .setup-header,
          .document-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default DocumentView;
